import { useCallback, useId } from 'react';
import { css, Theme } from '@emotion/react';
import MuiDialog, { DialogProps as MuiDialogProps } from '@mui/material/Dialog';
import { AppWindow } from '@phosphor-icons/react';
import { borderRadius } from 'client/Theme';
import { DialogContentProps, DialogContent } from './DialogContent';

export type DialogProps = {
  open: boolean;
  fullscreen?: boolean;
  width?: number;
  hideHeader?: boolean;
} & Omit<DialogContentProps, 'id'> &
  Omit<MuiDialogProps, 'title'>;

export const Dialog = ({
  open,
  title,
  description,
  icon = <AppWindow />,
  image,
  iconOrnament = null,
  primaryAction,
  secondaryAction,
  disablePrimary = false,
  hideHeader = false,
  dismissable = true,
  fullscreen = false,
  width = 600,
  actions,
  headerActions,
  onPrimary,
  onSecondary,
  onClose,
  children,
  ...other
}: DialogProps) => {
  const id = useId();

  const handleClose = useCallback(() => !!onClose && onClose(), [onClose]);

  const handleDialogClose = useCallback(
    (_: unknown, reason: string) => {
      if (!dismissable && reason === 'backdropClick') return;
      handleClose();
    },
    [dismissable, handleClose]
  );

  const dialogContentProps: DialogContentProps = {
    id,
    title,
    description,
    icon,
    image,
    iconOrnament,
    primaryAction,
    secondaryAction,
    disablePrimary,
    dismissable,
    actions,
    headerActions,
    onPrimary,
    onSecondary,
    onClose,
    children,
    hideHeader,
  };

  return (
    <MuiDialog
      open={open}
      onClose={handleDialogClose}
      aria-labelledby={`${id}.title`}
      aria-describedby={`${id}.description`}
      disableEscapeKeyDown={!dismissable}
      PaperProps={{ style: fullscreen ? {} : { width: `min(${width}px, calc(100vw - 2rem))` } }}
      css={[dialogStyle, fullscreen && fullscreenStyle]}
      {...other}
    >
      <DialogContent {...dialogContentProps} />
    </MuiDialog>
  );
};

const dialogStyle = (theme: Theme) => css`
  > .MuiDialog-container > .MuiPaper-root {
    padding: 8px;
    max-width: none;
    border-radius: ${borderRadius * 2}px;
    background-color: ${theme.color.background};
  }
`;

const fullscreenStyle = css`
  > .MuiDialog-container > .MuiPaper-root {
    margin: 0;
    width: 100vw;
    height: 100vh;
    max-width: none;
    max-height: none;
    border-radius: 0;
  }
`;
